/** @jsxImportSource @emotion/react */
import { css, SerializedStyles } from '@emotion/react';

import { styleguideColors as COLORS, styleguideFontWeight as FONT_WEIGHT } from '@/Client/Styles/kaiShikiTokens';
import { pxToRem } from '@/Client/Styles/pxToRem';
import { styleguideFontFamily as FONT } from '@kaiizen/kai-shiki/core';

/*
 * The property overview's shared pieces: the card each section sits in, its heading row, the mono
 * caption, buttons, the segmented toggle and the status chip. Each section component carries its
 * own styles object too; these are the ones they have in common, so a re-skin of the page changes
 * them in one place.
 */

const MONO_CAPTION = css`
  font-family: ${FONT.mono};
  font-size: ${pxToRem(9.5)};
  font-weight: ${FONT_WEIGHT.bold};
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

export type PropertyOverviewSharedStyles = {
  /** A section's card. */
  section: SerializedStyles;
  sectionHeader: SerializedStyles;
  sectionTitle: SerializedStyles;
  sectionCount: SerializedStyles;
  caption: SerializedStyles;
  button: SerializedStyles;
  primaryButton: SerializedStyles;
  segments: SerializedStyles;
  segment: SerializedStyles;
  selectedSegment: SerializedStyles;
  statusChip: SerializedStyles;
  statusChipByStatus: Record<'active' | 'completed', SerializedStyles>;
  dash: SerializedStyles;
};

export const DEFAULT_PROPERTY_OVERVIEW_SHARED_STYLES: PropertyOverviewSharedStyles = {
  section: css`
    label: PROPERTY_OVERVIEW_SECTION;
    background: ${COLORS.white};
    border: 1px solid ${COLORS.stroke};
    padding: 16px 18px;
  `,
  sectionHeader: css`
    label: PROPERTY_OVERVIEW_SECTION_HEADER;
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 10px;
    margin-bottom: 10px;
  `,
  sectionTitle: css`
    label: PROPERTY_OVERVIEW_SECTION_TITLE;
    ${MONO_CAPTION};
    margin: 0;
    color: ${COLORS.ink};
  `,
  sectionCount: css`
    label: PROPERTY_OVERVIEW_SECTION_COUNT;
    ${MONO_CAPTION};
    color: ${COLORS.midtone};
  `,
  caption: css`
    label: PROPERTY_OVERVIEW_CAPTION;
    ${MONO_CAPTION};
    color: ${COLORS.midtone};
  `,
  button: css`
    label: PROPERTY_OVERVIEW_BUTTON;
    ${MONO_CAPTION};
    padding: 8px 12px;
    border: 1px solid ${COLORS.stroke};
    background: ${COLORS.white};
    color: ${COLORS.ink};
    cursor: pointer;

    &:hover:not(:disabled) {
      border-color: ${COLORS.ink};
    }

    &:disabled {
      cursor: not-allowed;
      color: ${COLORS.textTertiary};
    }

    &:focus-visible {
      outline: 2px solid ${COLORS.ink};
      outline-offset: 2px;
    }
  `,
  primaryButton: css`
    label: PROPERTY_OVERVIEW_PRIMARY_BUTTON;
    background: ${COLORS.accent};
    border-color: ${COLORS.accentDeep};

    &:hover:not(:disabled) {
      border-color: ${COLORS.accentDeep};
      background: ${COLORS.accentDeep};
    }

    &:disabled {
      background: ${COLORS.sunken};
      border-color: ${COLORS.stroke};
    }
  `,
  segments: css`
    label: PROPERTY_OVERVIEW_SEGMENTS;
    display: inline-flex;
    margin-left: auto;
  `,
  segment: css`
    label: PROPERTY_OVERVIEW_SEGMENT;
    ${MONO_CAPTION};
    padding: 7px 11px;
    margin-left: -1px;
    border: 1px solid ${COLORS.stroke};
    background: ${COLORS.white};
    color: ${COLORS.midtone};
    cursor: pointer;

    &:first-of-type {
      margin-left: 0;
    }

    &:hover {
      color: ${COLORS.ink};
      border-color: ${COLORS.ink};
      position: relative;
      z-index: 1;
    }
  `,
  selectedSegment: css`
    label: PROPERTY_OVERVIEW_SEGMENT_SELECTED;
    background: ${COLORS.ink};
    border-color: ${COLORS.ink};
    color: ${COLORS.white};
    position: relative;
    z-index: 1;

    &:hover {
      color: ${COLORS.white};
    }
  `,
  /* A pill with a diamond - a square on its corner - as in PropertyOverview.html's .po-st. The
     diamond is solid once the work is done and hollow while it's still open (see statusChipByStatus). */
  statusChip: css`
    label: PROPERTY_OVERVIEW_STATUS_CHIP;
    ${MONO_CAPTION};
    font-size: ${pxToRem(9)};
    letter-spacing: 0.06em;
    line-height: 1.2;
    display: inline-flex;
    align-items: center;
    gap: 7px;
    padding: 5px 11px;
    border-radius: 999px;
    white-space: nowrap;

    &::before {
      content: '';
      width: 7px;
      height: 7px;
      box-sizing: border-box;
      flex-shrink: 0;
      background: currentColor;
      transform: rotate(45deg);
    }
  `,
  statusChipByStatus: {
    active: css`
      label: PROPERTY_OVERVIEW_STATUS_CHIP_ACTIVE;
      color: ${COLORS.info};
      background: ${COLORS.infoFill};

      &::before {
        background: none;
        border: 1.5px solid currentColor;
      }
    `,
    completed: css`
      label: PROPERTY_OVERVIEW_STATUS_CHIP_COMPLETED;
      color: ${COLORS.success};
      background: ${COLORS.successFill};
    `,
  },
  dash: css`
    label: PROPERTY_OVERVIEW_DASH;
    color: ${COLORS.textTertiary};
  `,
};
